import { useEffect, useRef, useState, useCallback } from "react";

interface TimerState {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

interface UseIntentionTimerProps {
  isActive: boolean;
  startDate: string;
  intentionName?: string;
}

/**
 * Custom hook for managing intention timer with proper cleanup and reset capabilities
 * Ensures that only one timer runs at a time and provides immediate visual feedback
 */
export const useIntentionTimer = ({ 
  isActive, 
  startDate, 
  intentionName 
}: UseIntentionTimerProps): TimerState => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [timer, setTimer] = useState<TimerState>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  const clearCurrentTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      
      // Also clear from localStorage if it exists
      const storedInterval = window.localStorage.getItem("intentionTimeInterval");
      if (storedInterval) {
        window.clearInterval(Number(storedInterval));
        window.localStorage.removeItem("intentionTimeInterval");
      }
    }
  }, []);

  const resetTimer = useCallback(() => {
    setTimer({
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    });
  }, []);

  const updateTimer = useCallback(() => {
    if (!startDate) return;
    
    const now = new Date();
    const startTime = new Date(startDate).getTime();
    const timeDifference = Math.max(0, now.getTime() - startTime);
    
    setTimer({
      seconds: Math.floor((timeDifference % (1000 * 60)) / 1000),
      minutes: Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60)),
      hours: Math.floor((timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      days: Math.floor(timeDifference / (1000 * 3600 * 24)),
    });
  }, [startDate]);

  useEffect(() => {
    // Always clear any existing timer first
    clearCurrentTimer();
    
    // Reset timer display if not active
    if (!isActive || !startDate) {
      resetTimer();
      return;
    }

    // Start new timer for active intention
    // Initial update for immediate feedback
    updateTimer();
    
    // Set up interval
    intervalRef.current = setInterval(updateTimer, 1000);
    
    // Store interval ID for cross-component cleanup
    window.localStorage.setItem("intentionTimeInterval", String(intervalRef.current));

    // Cleanup function
    return () => {
      clearCurrentTimer();
    };
  }, [isActive, startDate, intentionName, clearCurrentTimer, resetTimer, updateTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearCurrentTimer();
    };
  }, [clearCurrentTimer]);

  return timer;
}; 